import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Save, Upload, ExternalLink, Trash2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
    { k: "name", label: "Nombre completo" },
    { k: "position", label: "Cargo" },
    { k: "company", label: "Empresa" },
    { k: "phone", label: "Teléfono" },
    { k: "whatsapp", label: "WhatsApp (+país número)" },
    { k: "linkedin", label: "LinkedIn URL" },
    { k: "instagram", label: "Instagram URL" },
    { k: "twitter", label: "X / Twitter URL" },
    { k: "facebook", label: "Facebook URL" },
    { k: "website", label: "Sitio web" },
    { k: "card_slug", label: "Slug personalizado (opcional)", helper: "Solo minúsculas, números y guiones. Ej: maria-perez" },
];

export default function Profile() {
    const fileRef = useRef(null);
    const [search] = useSearchParams();
    const requiredHint = (search.get("required") || "").split(",").filter(Boolean);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/profile/me");
                setForm(data || {});
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar el perfil");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onPhotoFile = (file) => {
        if (!file) return;
        if (file.size > 1.5 * 1024 * 1024) {
            toast.error("La imagen supera 1.5 MB. Comprime y vuelve a intentar.");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => update("photo_url", String(reader.result || ""));
        reader.onerror = () => toast.error("No se pudo leer la imagen");
        reader.readAsDataURL(file);
    };

    const removePhoto = () => update("photo_url", "");

    const save = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        try {
            const payload = {};
            const keys = ["name","position","company","phone","whatsapp","linkedin","instagram","twitter","facebook","website","card_slug","bio","photo_url"];
            for (const k of keys) {
                const v = form[k];
                payload[k] = v == null ? "" : v;
            }
            const { data } = await api.patch("/profile/me", payload);
            setForm(data || {});
            toast.success("Perfil actualizado");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar");
        } finally {
            setSaving(false);
        }
    };

    const cardPath = form.card_slug ? `/c/${form.card_slug}` : (form.user_id ? `/c/${form.user_id}` : "");
    const cardUrl = cardPath ? `${window.location.origin}${cardPath}` : "";

    return (
        <Layout title="Mi perfil" subtitle="Edita los datos de tu tarjeta virtual" data-testid="profile-page">
            <div className="max-w-3xl mx-auto">
                {loading ? (
                    <div className="py-16 flex items-center justify-center text-slate-400 text-sm">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />Cargando perfil...
                    </div>
                ) : (
                    <form onSubmit={save} className="space-y-4" data-testid="profile-form">
                        {requiredHint.length > 0 && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-sm text-amber-900" data-testid="profile-required-banner">
                                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                                <div>
                                    <div className="font-medium">Completa tu tarjeta antes de compartirla</div>
                                    <div className="text-xs">Faltan: <strong>{requiredHint.join(", ")}</strong>. Guarda los cambios para volver a tu tarjeta.</div>
                                </div>
                            </div>
                        )}
                        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
                            <h2 className="font-display font-semibold text-slate-900 mb-4">Foto y biografía</h2>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                                <div className="w-24 h-24 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                                    {form.photo_url ? (
                                        <img src={form.photo_url} alt="Foto" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                                    ) : (
                                        <span className="text-slate-400 text-3xl font-semibold">{(form.name || "?")[0]?.toUpperCase()}</span>
                                    )}
                                </div>
                                <div className="flex-1 w-full">
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <input
                                            ref={fileRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => onPhotoFile(e.target.files?.[0])}
                                            data-testid="profile-photo-file"
                                        />
                                        <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="profile-photo-upload">
                                            <Upload className="w-4 h-4 mr-2" />Subir imagen
                                        </Button>
                                        {form.photo_url && (
                                            <Button type="button" variant="ghost" size="sm" onClick={removePhoto} className="text-red-600 hover:text-red-700" data-testid="profile-photo-remove">
                                                <Trash2 className="w-4 h-4 mr-2" />Quitar
                                            </Button>
                                        )}
                                    </div>
                                    <Label>URL de foto (alternativa)</Label>
                                    <Input
                                        value={typeof form.photo_url === "string" && form.photo_url.startsWith("data:") ? "" : (form.photo_url || "")}
                                        onChange={(e) => update("photo_url", e.target.value)}
                                        placeholder="https://..."
                                        data-testid="profile-photo-url"
                                    />
                                </div>
                            </div>
                            <div className="mt-4">
                                <Label>Bio</Label>
                                <Textarea rows={3} value={form.bio || ""} onChange={(e) => update("bio", e.target.value)} placeholder="Comparte qué haces, tu propuesta, contacto preferido..." data-testid="profile-bio" />
                            </div>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
                            <h2 className="font-display font-semibold text-slate-900 mb-4">Datos de contacto</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {FIELDS.map((f) => (
                                    <div key={f.k} className={f.k === "card_slug" ? "sm:col-span-2" : ""}>
                                        <Label>{f.label}</Label>
                                        <Input
                                            value={form[f.k] || ""}
                                            onChange={(e) => update(f.k, e.target.value)}
                                            data-testid={`profile-${f.k}`}
                                        />
                                        {f.helper && <p className="text-xs text-slate-500 mt-1">{f.helper}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
                            <h2 className="font-display font-semibold text-slate-900 mb-3">Tu tarjeta pública</h2>
                            {cardUrl ? (
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <code className="text-xs sm:text-sm text-orange-700 bg-orange-50 px-2 py-1 rounded break-all" data-testid="profile-card-url">{cardUrl}</code>
                                    <a href={cardPath} target="_blank" rel="noreferrer" className="text-sm text-orange-700 hover:underline inline-flex items-center" data-testid="profile-card-open">
                                        <ExternalLink className="w-4 h-4 mr-1" />Abrir
                                    </a>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-500">Guarda tu perfil para generar el link.</p>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-2 sticky bottom-2 sm:static">
                            <Button type="submit" disabled={saving} data-testid="profile-save">
                                {saving ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
                                ) : (
                                    <><Save className="w-4 h-4 mr-2" />Guardar cambios</>
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </div>
        </Layout>
    );
}
